import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { NoCardEntry } from 'components/league/NoCardEntry';
import {
  getFixtureQueryVariables,
  isFixtureLive,
  isFixtureOpened,
} from 'lib/so5';

import type {
  PlayPageBaseQuery,
  PlayPageBaseQueryVariables,
} from './__generated__/index.graphql';

const query = gql`
  query PlayPageBaseQuery(
    $type: So5State
    $slug: String
    $sport: Sport!
    $eventType: So5FixtureEvent!
  ) {
    so5 {
      nextSo5Fixtures: so5Fixtures(
        aasmStates: ["opened", "started"]
        eventType: $eventType
        sport: $sport
        first: 1
      ) {
        nodes {
          slug
        }
      }
      so5Fixture(
        type: $type
        slug: $slug
        eventType: $eventType
        sport: $sport
      ) {
        slug
        sport
        type
        ...isFixtureLive_so5Fixture
        ...isFixtureOpened_so5Fixture
      }
    }
  }
  ${isFixtureLive.fragments.so5Fixture}
  ${isFixtureOpened.fragments.so5Fixture}
` as TypedDocumentNode<PlayPageBaseQuery, PlayPageBaseQueryVariables>;

const Wrapper = styled(Vertical)`
  position: relative;
  padding: var(--unit);

  @media ${tabletAndAbove} {
    padding: var(--triple-unit);
  }
`;

const NoCardEntryContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: auto;
`;

type Props = {
  fixture: string;
  eventType: string;
  children: ReactNode;
};

export const PlayPageBase = ({ eventType, fixture, children }: Props) => {
  const { sport } = useSportContext();
  const { data } = useQuery(query, {
    variables: {
      sport,
      ...getFixtureQueryVariables(fixture),
      eventType: eventType.toUpperCase() as So5FixtureEvent,
    },
  });

  if (!data?.so5?.so5Fixture) {
    return null;
  }

  const { so5Fixture, nextSo5Fixtures } = data.so5;

  const noCardRouteEnabled = so5Fixture.type === So5FixtureEvent.CLASSIC;
  const isLive = so5Fixture ? isFixtureLive(so5Fixture) : false;
  const isUpcoming = isFixtureOpened(so5Fixture);
  const isNextUpcoming =
    isUpcoming && nextSo5Fixtures?.nodes[0]?.slug === so5Fixture.slug;

  return (
    <Wrapper gap={2}>
      {children}
      {!!noCardRouteEnabled && !!(isNextUpcoming || isLive) && (
        <FullWidthContainer>
          <NoCardEntryContainer>
            <NoCardEntry
              sport={so5Fixture.sport}
              eventType={so5Fixture.type}
              alwaysShow={isNextUpcoming}
            />
          </NoCardEntryContainer>
        </FullWidthContainer>
      )}
    </Wrapper>
  );
};
